import React from "react"
import { useRouter } from "next/router"
import { observer } from "mobx-react-lite"
import { useTheme, CSSObject } from "@emotion/react"
import { AppTheme } from "pitch45-common/theme/app-theme.types"
import { mediaQueries } from "pitch45-common/theme/layout"
import ContentLayout from "../../components/content-layout/content-layout"
import type { Page } from "../../types/page"
import { useQuery } from "@tanstack/react-query"
import { unitSpacing } from "../../theme/spacing"
import { getSnapshot } from "mobx-state-tree"
import { InferGetServerSidePropsType } from "next"
import { CircularProgress } from "@mui/material"
import { PitchViewer } from "../../components/pitch-viewer/pitch-viewer"
import { useServerSideStoreHydration } from "../../utils/use-server-side-store-hydration"
import { GenericPageHead } from "../../components/generic-page-head/generic-page-head"
import { wrapSSR } from "../../utils/wrap-ssr"
import { PlaylistOverview } from "../../components/pitch-viewer/playlist-overview"
import EmptyVideoPlayer from "../../components/pitch-viewer/empty-video-player"
import Head from "next/head"
import { useStores } from "pitch45-common/stores/root-store-context"
import { PitchViewerStore } from "pitch45-common/stores/pitch-viewer-store"
import { AssetDimensions } from "pitch45-common/models/asset"
import { ContentVisibility } from "pitch45-common/models/content-visibility"
import { Pitch } from "pitch45-common/models/pitch"
import { createSsrRootStore } from "../../utils/create-ssr-root-store"
import { getApiErrorProblemKind } from "pitch45-common/utils/error"

export function getStyles(theme: AppTheme) {
  const styles = {
    CONTAINER: {
      background: theme.colors.background,
      display: "flex",
      justifyContent: "center",
      height: "100%",
      margin: "0 auto",
      [mediaQueries.TABLET]: {
        width: "100%",
        flexDirection: "column",
        alignItems: "center",
      },
    },
  }
  return styles as { [key in keyof typeof styles]: CSSObject }
}

function parseParams(params: string[]) {
  let pitchId: string | undefined = params[0]
  let playlistId = undefined as string | undefined
  if (params && params.length === 2) {
    playlistId = params[1]
  }

  // this supports the url format /watch/playlist/{playlistId} -- hosting a separate page for that
  // caused unwanted reloads as the url changed when transitioning from the first to second video
  // in the playlist
  if (pitchId === "playlist") {
    pitchId = undefined
  }

  return { pitchId, playlistId }
}

async function fetchViewingData(
  pitchViewerStore: PitchViewerStore,
  pitchId: string | undefined,
  playlistId: string | undefined,
) {
  if (playlistId) {
    return await pitchViewerStore.fetchPlaylist({ pitchId, playlistId })
  } else {
    if (!pitchId) {
      throw new Error("No pitch id provided")
    }

    return await pitchViewerStore.fetchSinglePitch(pitchId)
  }
}

export const getServerSideProps = wrapSSR(async ({ params }) => {
  const { pitchId, playlistId } = parseParams(params.params as string[])
  const rootStore = createSsrRootStore()
  const { pitchViewerStore } = rootStore

  const viewingData = await fetchViewingData(pitchViewerStore, pitchId, playlistId)
  const props = {
    rootStoreSnapshot: getSnapshot(rootStore),
    ...(viewingData ? { viewingData } : {}),
  }

  return {
    props: JSON.parse(JSON.stringify(props)),
  } as {
    props: typeof props
  }
})

const PitchViewerPage: Page<InferGetServerSidePropsType<typeof getServerSideProps>> = observer(
  function PitchViewerPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const router = useRouter()
    const params = router.query.params
    const { pitchId, playlistId } = parseParams(params as string[])
    const { pitchViewerStore, assetStore } = useStores()

    const forceAuth = playlistId
      ? props.viewingData?.playlistId
        ? props.viewingData?.visibility !== ContentVisibility.External
        : true
      : false

    const { isReady } = useServerSideStoreHydration({
      storeSnapshot: props.rootStoreSnapshot,
      viewingData: props.viewingData,
      fetch: () => refetch(),
      // force auth for playlists that are not fully external
      forceAuth,
    })

    const playlist = pitchViewerStore.playlist
    const pitches = pitchViewerStore.pitches.filter((p): p is Pitch => Boolean(p))

    const { isFetching, refetch } = useQuery(
      // don't use playlist id AND pitch id because that causes us to refetch unnecessarily
      // when replacing the pitch id in the router
      ["fetchViewingData", playlistId ?? pitchId],
      () => {
        return fetchViewingData(pitchViewerStore, pitchId, playlistId)
      },
      {
        // vs initialData
        placeholderData: props.viewingData,
        onError(e) {
          if (getApiErrorProblemKind(e) === "not-found") {
            pitchViewerStore.reset()
          }
        },
        enabled: isReady,
      },
    )

    const theme = useTheme() as AppTheme
    const styles = getStyles(theme)

    const getContent = () => {
      // If we didn't get the data during SSR, then show a loading spinner while we attempt a silent auth.
      // Once the silent auth finishes, if we get new data, we can show it, otherwise the user will get
      // the EmptyVideoPlayer
      if (!isReady || isFetching) {
        return (
          <>
            <div css={styles.CONTAINER}>
              <CircularProgress sx={{ margin: unitSpacing.quad, color: theme.colors.primary }} />
            </div>
          </>
        )
      }

      if (pitches?.length > 0) {
        return <PitchViewer isLoading={isFetching} />
      } else if (playlist) {
        return <PlaylistOverview />
      } else {
        return (
          <EmptyVideoPlayer
            message={playlistId ? "Playlist not available" : "Video not available"}
          />
        )
      }
    }

    const url = new URL(`${process.env.NEXT_PUBLIC_WEB_APP_BASE_URL}${router.asPath}`)
    const canonicalUrl = `${url.origin}${url.pathname}`
    const defaultOgImage = `${process.env.NEXT_PUBLIC_WEB_APP_BASE_URL}/imgs/og/logo.png`

    const openGraphDetails = (() => {
      if (playlist) {
        const assetId =
          playlist.coverAssetId ??
          (playlist.name === playlist.autogeneratedImageName
            ? playlist.autogeneratedCoverAssetId
            : undefined)
        return {
          title: playlist.name,
          description: playlist.description,
          image: assetStore.getPublicUri(assetId, AssetDimensions.LargeSquare) ?? defaultOgImage,
        }
      }
      if (pitches.length) {
        return {
          title: pitches[0].name,
          description: pitches[0].description,
          image:
            assetStore.getPublicUri(pitches[0].thumbnailAssetId, AssetDimensions.LargeSquare) ??
            defaultOgImage,
        }
      }
      return undefined
    })()

    return (
      <ContentLayout showSigninHeader={forceAuth} isAuthReady={isReady}>
        {openGraphDetails ? (
          <Head>
            <title>Loop :45 - Watch Videos</title>
            <meta name="og:site_name" content="Loop :45" />
            <meta name="og:url" content={canonicalUrl} />
            <meta name="og:description" content={openGraphDetails.description} />
            <meta name="og:title" content={openGraphDetails.title} />
            <meta name="og:image" content={openGraphDetails.image} />
          </Head>
        ) : (
          <GenericPageHead />
        )}
        {getContent()}
      </ContentLayout>
    )
  },
)

export default PitchViewerPage
